import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { FileSyncOutlined } from "@ant-design/icons";

import PolicySelect from "../shared/PolicySelect";
import CarrierContactBar from "../shared/CarrierContactBar";
import CarrierPortalCredential from "../shared/CarrierPortalCredential";
import VerificationActionButton from "../shared/VerificationActionButton";
import RequestedEstimatesPanel from "../shared/RequestedEstimatesPanel";
import PolicyInformationCollapse from "../shared/PolicyInformationCollapse";
import BreakdownFailureCard from "../Breakdown/BreakdownFailureCard";
import useRequestComposer from "../../../InsuranceRequests/useRequestComposer";
import { REQUEST_TYPES } from "../../../InsuranceRequests/requestTypes";

const CLAIM = REQUEST_TYPES.claim_followup;

/**
 * Claims tab of the insurance modal — one policy's claim follow-up, for the
 * FTC user chasing it and the office that asked.
 *
 * A lean sibling of the Estimates tab, built from the same parts in the same
 * places (policy picker, carrier playbook + portal login, attention card and
 * the request button on the left; outcome control + policy information on the
 * right) so the eye already knows the layout. What it leaves out is everything
 * about FETCHING: a claim follow-up is never automated, so there are no cases
 * to list, no AI fetch, no PDF upload and no latest-fetch card. The content of
 * this tab is the request itself — the claim(s) the office asked us to chase —
 * which the Estimates tab keeps in its sidebar and this tab makes the main pane.
 *
 * The carrier bar is asked for the ESTIMATE playbook on purpose: a claim is
 * chased the way an estimate is — same phone line, same portal, same office
 * identifiers — which is also why the server bills and routes the two as one
 * family (see `ftc_action_for` in clinics/services/verification_requests.py).
 */
export default function ClaimFollowupsPane({
  patient,
  // The patient's own office, for the office-scoped portal login.
  officeId,
  officeIdentity,
  // Viewer mode — see InsuranceMasterModal. Hides the carrier bar, the saved
  // portal login and the request rail; the outcome control stays.
  hideVerificationTools = false,
  patientPolicies,
  // Pre-select this policy on open (an FTC claim chip, a deep link).
  defaultSelectedPolicyId,
  isFetchingPolicies,
  carrierOptions,
  isLoadingCarriers,
  onPoliciesChanged,
  onNoteSaved,
  onRequestAddPolicy,
}) {
  const patientId = patient && patient.id;
  const [selectedPolicyID, setSelectedPolicyID] = useState(undefined);
  const [policyOverrides, setPolicyOverrides] = useState({});
  const [policyCollapseActiveKey, setPolicyCollapseActiveKey] = useState([]);
  // Bumped when the composer sends a request from this tab, so the claim rows
  // appear without closing and reopening the modal.
  const [refreshTick, setRefreshTick] = useState(0);
  const handleSubmitted = useCallback(function () {
    setRefreshTick(function (n) { return n + 1; });
  }, []);
  const { openComposer, composer } = useRequestComposer({ overModal: true, onSubmitted: handleSubmitted });

  // Same pre-select rule as the other tabs: the caller's policy when the
  // patient actually holds it, else the first policy.
  useEffect(
    function () {
      if (selectedPolicyID) return;
      if (Array.isArray(patientPolicies) && patientPolicies.length > 0) {
        const preferred =
          defaultSelectedPolicyId != null && patientPolicies.some((p) => p.id === defaultSelectedPolicyId)
            ? defaultSelectedPolicyId
            : patientPolicies[0].id;
        setSelectedPolicyID(preferred);
      }
    },
    [patientPolicies, selectedPolicyID, defaultSelectedPolicyId],
  );

  // The selected policy with this tab's unsaved Policy Information edits on top,
  // so the outcome buttons and the note header follow an edit immediately.
  const selectedPolicy = useMemo(
    function () {
      if (!selectedPolicyID || !Array.isArray(patientPolicies)) return undefined;
      const base = patientPolicies.find((p) => p.id === selectedPolicyID);
      if (!base) return undefined;
      const ov = policyOverrides[base.id];
      return ov ? { ...base, ...ov } : base;
    },
    [selectedPolicyID, patientPolicies, policyOverrides],
  );

  const hasPolicies = Array.isArray(patientPolicies) && patientPolicies.length > 0;

  const sidebar = (
    <div style={{ width: 300, borderRight: "1px solid #f0f0f0", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* One scrolling column: with no list to share the sidebar with, the
          top block simply takes the height and scrolls what doesn't fit. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", background: "#fafbfc" }}>
        <div data-onboarding-id="claims-select-policy" style={{ padding: "20px 15px 14px 15px", flexShrink: 0 }}>
          <PolicySelect
            patientPolicies={patientPolicies}
            value={selectedPolicyID}
            onChange={setSelectedPolicyID}
            onRequestAddPolicy={onRequestAddPolicy}
            isFetchingPolicies={isFetchingPolicies}
          />
        </div>

        {!hideVerificationTools && (
          <>
            <CarrierContactBar policy={selectedPolicy} identity={officeIdentity} taskType="estimate" />
            <CarrierPortalCredential policy={selectedPolicy} officeId={officeId} />
          </>
        )}

        {/* The per-policy attention note, shared with the other tabs. No noun:
            nothing is ever fetched for a claim, so a fetch failure shown here
            belongs to a breakdown or estimate run and the card names it from
            the job rather than calling it a claim's. */}
        <div style={{ padding: "0 15px 12px 15px", flexShrink: 0 }}>
          <BreakdownFailureCard
            policyId={selectedPolicyID}
            patientId={patientId}
            patientTags={(patient && patient.tags) || []}
            policyNeedsAttention={!!(selectedPolicy && selectedPolicy.needs_attention_at)}
            manualAttentionNote={selectedPolicy && selectedPolicy.attention_note}
            manualAttentionNoteSetAt={selectedPolicy && selectedPolicy.attention_note_set_at}
            manualAttentionNoteSource={selectedPolicy && selectedPolicy.attention_note_source}
            infoAsk={selectedPolicy && selectedPolicy.info_ask}
            onNoteSaved={onNoteSaved}
          />
        </div>
      </div>

      {/* Sticky action rail — the request button only. There is nothing to
          add manually or upload for a follow-up. */}
      {!hideVerificationTools && (
        <div data-onboarding-id="claims-request-action" style={{ padding: "20px 15px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 10 }}>
          <VerificationActionButton
            operationType={CLAIM.value}
            requestNoun={CLAIM.noun}
            patientId={patientId}
            capabilityKnown
            policySelected={!!selectedPolicyID}
            hasScraper={false}
            manualTooltip="Our team will call the carrier about this claim as soon as possible."
            onOpenComposer={() => {
              openComposer(patient, { policyId: selectedPolicyID, requestType: CLAIM.value });
            }}
          />
        </div>
      )}
    </div>
  );

  const rightPane = (
    <div data-onboarding-id="claims-pane-right" style={{ flex: 1, backgroundColor: "#fafbfc", overflowY: "auto", position: "relative", display: "flex", flexDirection: "column" }}>
      {/* No Mark Follow-up Done / Details Needed by Smilepass buttons here: a claim is
          answered item by item with Record result (Paid, Payment being
          processed, Resubmit to Carrier...), and the request completes when every
          claim has its answer (RequestedEstimatesPanel). */}

      <div style={{ padding: "20px 24px 8px 24px" }}>
        <PolicyInformationCollapse
          officeIdentity={officeIdentity}
          selectedPolicy={selectedPolicy}
          patient={patient}
          policyCollapseActiveKey={policyCollapseActiveKey}
          setPolicyCollapseActiveKey={setPolicyCollapseActiveKey}
          carrierOptions={carrierOptions}
          isLoadingCarriers={isLoadingCarriers}
          setPolicyOverrides={setPolicyOverrides}
          onPoliciesChanged={onPoliciesChanged}
        />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px 24px" }}>
        {selectedPolicyID ? (
          // What the office asked us to chase, and what it asked for on the
          // follow-ups already done: amount, date of service, patient, provider,
          // CDA/iTrans number, procedures and the claim form (previewed in
          // place). The panel reads only this policy's CLAIM rows — an estimate
          // open on the same policy is not shown here.
          <RequestedEstimatesPanel
            policyId={selectedPolicyID}
            requestType={CLAIM.value}
            refreshKey={refreshTick}
            emptyText="No claim follow-up has been requested on this policy."
            readOnly={hideVerificationTools}
            carrierName={selectedPolicy ? selectedPolicy.insurance_name_display || selectedPolicy.insurance_name : null}
            patientId={patientId}
            onPolicyPatched={onNoteSaved}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40, textAlign: "center" }}>
            <FileSyncOutlined style={{ fontSize: 64, color: "#848696", opacity: 0.2, marginBottom: 20 }} />
            <h2 style={{ color: "#848696", fontWeight: 400 }}>Claim Follow-ups</h2>
            <p style={{ color: "#848696", maxWidth: 460 }}>
              {hasPolicies
                ? "Select a policy to see the claim the office asked us to chase, and to mark the follow-up done."
                : "No insurance policy on file for this patient. Add a policy to request a claim follow-up."}
            </p>
            {!hasPolicies && onRequestAddPolicy ? (
              <Button type="primary" size="small" onClick={onRequestAddPolicy}>
                + Add Policy
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div style={{ display: "flex", width: "100%", height: "100%" }} data-onboarding-id="claims-pane">
        {sidebar}
        {rightPane}
      </div>

      {/* The request composer, stacked over the modal — see useRequestComposer. */}
      {composer}
    </>
  );
}
